import React, { useMemo } from "react";
import { Checkbox } from "@mui/material";
import { PiSquaresFour } from "react-icons/pi";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import Progress from "./components/Progress";

/* ref_data from update API has previous_progress & progress. When milestone not in ref_data: show 0 to progress (green), thumb at progress. When in ref_data: show previous_progress (green), progress (blue), thumb at progress/slider. */
const MilestoneCard = ({
  projectDetails,
  refDataMilestones = [],
  selectedMilestones,
  setSelectedMilestones,
  milestoneProgress = {},
  onProgressChange,
  milestoneNotes = {},
  onNotesChange,
  disabled = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const refDataByMilestoneId = useMemo(() => {
    const map = {};
    (refDataMilestones || []).forEach((m) => {
      const id = m.id;
      if (id == null) return;
      map[id] = m;
      if (typeof id === "number") map[String(id)] = m;
      else if (typeof id === "string") map[Number(id)] = m;
    });
    return map;
  }, [refDataMilestones]);

  const refDataIds = useMemo(
    () => new Set((refDataMilestones || []).map((m) => m.id).filter((id) => id != null)),
    [refDataMilestones]
  );

  const getProgressInfo = (item) => {
    const id = item.id;
    return refDataByMilestoneId[id] ?? refDataByMilestoneId[Number(id)] ?? refDataByMilestoneId[String(id)];
  };

  const data = projectDetails;
  const handleCheckboxChange = (milestone) => {
    if (disabled) return;
    if (refDataIds.has(milestone.id)) return;
    const isSelected = selectedMilestones.some((m) => m.id === milestone.id);
    if (isSelected) {
      setSelectedMilestones((prev) =>
        prev.filter((m) => m.id !== milestone.id),
      );
    } else {
      setSelectedMilestones((prev) => [...prev, milestone]);
    }
  };

  const handleProgressChangeLocked = (id, value, prev) => {
    const safeValue = Math.max(value, prev);
    onProgressChange?.(id, safeValue);
  };

  const renderMilestoneRow = (item) => {
    const isChecked = selectedMilestones.some((m) => m.id === item.id);
    const isLocked = refDataIds.has(item.id);

    const progressInfo = getProgressInfo(item);
    const inRefData = !!progressInfo;

    const currentValue = inRefData
      ? (milestoneProgress[item.id] ?? progressInfo?.progress ?? 0)
      : (milestoneProgress[item.id] ?? item.progress ?? item.milestone_percentage ?? 0);

    const prev = inRefData
      ? (progressInfo?.previous_progress ?? progressInfo?.progress ?? 0)
      : (item.progress ?? item.milestone_percentage ?? 0);

    const trackBg = `linear-gradient(to right, #22c55e 0%, #22c55e ${prev}%, #2563eb ${prev}%, #2563eb ${currentValue}%, #bfc5ce40 ${currentValue}%, #bfc5ce40 100%)`;
    const current = Number(currentValue) || 0;
    return (
      <div
        key={item.id}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          padding: "12px",
          borderBottom: "1px solid #D7D8D9",
          backgroundColor: "#fff",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Checkbox
            checked={isChecked}
            disabled={isLocked}
            readOnly={isLocked}
            onChange={() => handleCheckboxChange(item)}
            size="small"
            sx={{ padding: "0 4px 0 0" }}
          />
          <span
            style={{
              fontSize: "14px",
              color: "#949DAA",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <PiSquaresFour size={18} /> {item.tags || "-"}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img
              src={
                item?.milestone_image?.path ||
                "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b"
              }
              alt={item.milestone_name}
              style={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                objectFit: "cover",
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: "14px", color: "#0d0d0e", fontWeight: 500 }}>
              {item.milestone_name}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
            <label style={{ fontSize: "12px", color: "#949DAA" }}>Planned Progress</label>
            <Progress completion={item.milestone_percentage ?? 0} type={item.status} hideProgress={false} showPercent={true} />
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 0,
                position: "relative",
                display: "flex",
                alignItems: "center",
                minHeight: 18,
              }}
            >
              <div
                style={{
                  height: 7,
                  width: "100%",
                  borderRadius: 9999,
                  flexShrink: 0,
                  background: trackBg,
                }}
                aria-hidden
              />
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={current}
                disabled={disabled}
                onChange={(e) =>
                  handleProgressChangeLocked(item.id, Number(e.target.value), prev)
                }
                className={`milestone-progress-slider-overlay ${current <= prev ? "locked-left" : ""}`}
              />
            </div>
            <span style={{ fontSize: "14px", fontWeight: 500, color: "#2563eb", flexShrink: 0 }}>
              {current}%
            </span>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label
            style={{
              fontSize: "12px",
              color: "#949DAA",
            }}
          >
            Additional Notes
          </label>
          <input
            type="text"
            placeholder="Notes (optional)"
            value={milestoneNotes[item.id] ?? ""}
            disabled={disabled}
            onChange={(e) =>
              onNotesChange?.(item.id, e.target.value)
            }
            style={{
              width: "100%",
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              padding: "8px 12px",
              fontSize: "14px",
              color: "#0d0d0e",
              boxSizing: "border-box",
            }}
          />
        </div>
      </div>
    );
  };

  const renderDesktopRow = (item) => {
    const isChecked = selectedMilestones.some((m) => m.id === item.id);
    const isLocked = refDataIds.has(item.id);

    const progressInfo = getProgressInfo(item);
    const inRefData = !!progressInfo;

    const currentValue = inRefData
      ? (milestoneProgress[item.id] ?? progressInfo?.progress ?? 0)
      : (milestoneProgress[item.id] ?? item.progress ?? item.milestone_percentage ?? 0);

    const prev = inRefData
      ? (progressInfo?.previous_progress ?? progressInfo?.progress ?? 0)
      : (item.progress ?? item.milestone_percentage ?? 0);

    const trackBg = `linear-gradient(to right, #22c55e 0%, #22c55e ${prev}%, #2563eb ${prev}%, #2563eb ${currentValue}%, #bfc5ce40 ${currentValue}%, #bfc5ce40 100%)`;
    const current = Number(currentValue) || 0;

    return (
      <div
        key={item.id}
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, auto) 2fr 1fr 1fr 2fr 2fr",
          alignItems: "center",
          padding: "12px 16px",
          borderRadius: "12px",
          gap: "8px",
          minWidth: 0,
        }}
      >
        <div style={{ width: 40, flexShrink: 0 }}>
          <Checkbox
            checked={isChecked}
            disabled={isLocked}
            readOnly={isLocked}
            onChange={() => handleCheckboxChange(item)}
            size="small"
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img
            src={
              item?.milestone_image?.path ||
              "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b"
            }
            alt={item.milestone_name}
            style={{
              width: 40,
              height: 40,
              borderRadius: "8px",
              objectFit: "cover",
            }}
          />
          <div style={{ fontSize: "14px", color: "#0d0d0e" }}>
            {item.milestone_name}
          </div>
        </div>

        <div style={{ fontSize: "14px", color: "#6b7280", display: "flex", alignItems: "center", gap: 4 }}>
          <PiSquaresFour size={18} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.tags || "-"}</span>
        </div>

        <div style={{ fontSize: "14px", minWidth: 0 }}>
          <Progress completion={item.milestone_percentage ?? 0} type={item.status} hideProgress={false} showPercent={true} />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0, marginRight: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div style={{ position: "relative", flex: 1, minWidth: 0, minHeight: 18, display: "flex", alignItems: "center" }}>
              <div style={{ height: 7, width: "100%", borderRadius: 9999, flexShrink: 0, background: trackBg }} aria-hidden />
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={current}
                disabled={disabled}
                onChange={(e) =>
                  handleProgressChangeLocked(item.id, Number(e.target.value), prev)
                }
                className={`milestone-progress-slider-overlay ${current <= prev ? "locked-left" : ""}`}
                style={{ width: "100%" }}
              />
            </div>
            <span style={{ fontSize: "14px", fontWeight: 500, color: "#2563eb", flexShrink: 0 }}>
              {current}%
            </span>
          </div>
        </div>

        <div style={{ minWidth: 0 }}>
          <input
            type="text"
            placeholder="Notes (optional)"
            value={milestoneNotes[item.id] ?? ""}
            disabled={disabled}
            onChange={(e) =>
              onNotesChange?.(item.id, e.target.value)
            }
            style={{
              width: "100%",
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              padding: "8px 12px",
              fontSize: "14px",
              color: "#0d0d0e",
              boxSizing: "border-box",
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div style={{ width: "100%" }}>
      <p
        style={{
          fontWeight: 500,
          fontFamily: "Inter Tight",
          marginBottom: 12,
        }}
      >
        Impacted Milestones
      </p>

      <div style={{ marginTop: 16 }}>
        {isMobile ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {data?.milestone?.map((item) => renderMilestoneRow(item))}
          </div>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, auto) 2fr 1fr 1fr 2fr 2fr",
                padding: "12px 16px",
                fontSize: "14px",
                color: "#949daa",
                gap: "8px",
              }}
            >
              <div style={{ width: 40 }} />
              <div>Milestone</div>
              <div>Type</div>
              <div>Planned Progress</div>
              <div style={{ marginRight: "10px" }}>Progress</div>
              <div>Additional Notes</div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              {data?.milestone?.map((item) => renderDesktopRow(item))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MilestoneCard;
